import { useEffect, useState, useCallback, useRef } from "react";

export function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) && !window.MSStream;
}
export function isIOSSafari() {
  if (!isIOS()) return false;
  const ua = navigator.userAgent;
  return !/CriOS|FxiOS|EdgiOS|OPiOS|GSA|FBAN|FBAV/i.test(ua);
}
export function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent);
}
export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

/**
 * Global hook used by the install banner, menu link, and Profile row.
 * Listens once for `beforeinstallprompt` and `appinstalled` so every consumer
 * gets a synchronized view of "can the user install right now?".
 */
let bipEvent = null;          // module-level cache of the deferred prompt
const subscribers = new Set();
function notify() { subscribers.forEach((fn) => fn(bipEvent)); }

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    bipEvent = e;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    bipEvent = null;
    notify();
  });
}

export function usePWAInstall() {
  const [prompt, setPrompt] = useState(bipEvent);
  const [standalone, setStandalone] = useState(isStandalone());
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const fn = (e) => setPrompt(e);
    subscribers.add(fn);
    // Also listen for the appinstalled event for our local "installed" flag
    const onInstalled = () => { setInstalled(true); setStandalone(true); };
    window.addEventListener("appinstalled", onInstalled);
    const mql = window.matchMedia?.("(display-mode: standalone)");
    const onChange = (ev) => setStandalone(ev.matches);
    mql?.addEventListener?.("change", onChange);
    return () => {
      subscribers.delete(fn);
      window.removeEventListener("appinstalled", onInstalled);
      mql?.removeEventListener?.("change", onChange);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!prompt) return { outcome: "unavailable" };
    try {
      prompt.prompt();
      const choice = await prompt.userChoice;
      bipEvent = null; setPrompt(null);
      return choice;
    } catch {
      return { outcome: "error" };
    }
  }, [prompt]);

  return {
    canInstall: !!prompt && !standalone,
    isStandalone: standalone,
    installed,
    isIOS: isIOS(),
    isIOSSafari: isIOSSafari(),
    isAndroid: isAndroid(),
    promptInstall,
  };
}

/* ---------- Push notification hook ---------- */
import { api } from "./config";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function usePushNotifications() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const checkedRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const ok =
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window;
    setSupported(ok);
    if (!ok || checkedRef.current) return;
    checkedRef.current = true;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
      } catch { /* noop */ }
    })();
  }, []);

  const subscribe = useCallback(async () => {
    if (!supported) return { ok: false, reason: "unsupported" };
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") return { ok: false, reason: "denied" };
      const { data } = await api.get("/push/vapid-public-key");
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
        user_agent: navigator.userAgent,
      });
      setSubscribed(true);
      // Fire a confirmation push so the user sees it immediately
      api.post("/push/test").catch(() => {});
      return { ok: true };
    } catch (e) {
      return { ok: false, reason: e?.message || "error" };
    } finally { setBusy(false); }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return { ok: false };
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await sub.unsubscribe();
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
      }
      setSubscribed(false);
      return { ok: true };
    } finally { setBusy(false); }
  }, [supported]);

  return { supported, permission, subscribed, busy, subscribe, unsubscribe };
}
